if (!customElements.get('theme-tabs')) {
  let tabsAccordionCounter = 0;

  class ThemeTabs extends HTMLElement {
    constructor() {
      super();
      this.tablist = null;
      this.tabs = [];
      this.panels = [];
      this.mediaQuery = null;
      this._accordionItems = [];
      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabPrefetch = this.handleTabPrefetch.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleAccordionTriggerClick = this.handleAccordionTriggerClick.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
    }

    connectedCallback() {
      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = [...this.querySelectorAll('[role="tab"]')];

      if (!this.tablist || this.tabs.length === 0) return;

      this.panels = this.tabs.map(tab => {
        const panelId = tab.getAttribute('aria-controls');
        const tabId = tab.id;
        let panel = this.querySelector(`#${panelId}`);

        if (!panel) {
          const rawId = tab.getAttribute('data-tab-id') || panelId.replace(/-panel$/, '');
          panel = this.querySelector(`[data-tabs-panel="${rawId}"]`);
          if (panel) panel.id = panelId;
        }

        if (panel) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tabId);
          if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '0');
        }

        return panel;
      }).filter(Boolean);

      if (this.dataset.responsive) {
        const breakpoint = parseInt(this.dataset.breakpoint) || 1024;
        this.mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
        this.mediaQuery.addEventListener('change', this.handleMediaChange);
        this.handleMediaChange(this.mediaQuery);
      } else {
        this.setupTabs();
      }
    }

    disconnectedCallback() {
      this.mediaQuery?.removeEventListener('change', this.handleMediaChange);
      this.teardownTabs();
      this.teardownAccordion();
    }

    handleMediaChange(event) {
      if (event.matches) {
        this.teardownTabs();
        this.setupAccordion();
      } else {
        this.teardownAccordion();
        this.setupTabs();
      }
    }

    setupTabs() {
      this.tablist.removeAttribute('hidden');

      this.panels.forEach(p => p.setAttribute('hidden', ''));

      this.tabs.forEach(tab => {
        tab.addEventListener('click', this.handleTabClick);
        tab.addEventListener('pointerenter', this.handleTabPrefetch);
        tab.addEventListener('focus', this.handleTabPrefetch);
      });
      this.tablist.addEventListener('keydown', this.handleKeydown);

      const preselected = this.tabs.find(t => t.getAttribute('aria-selected') === 'true');
      this.activate(preselected ?? this.tabs[0], false);
    }

    teardownTabs() {
      this.tabs.forEach(tab => {
        tab.removeEventListener('click', this.handleTabClick);
        tab.removeEventListener('pointerenter', this.handleTabPrefetch);
        tab.removeEventListener('focus', this.handleTabPrefetch);
      });
      this.tablist?.removeEventListener('keydown', this.handleKeydown);
    }

    setupAccordion() {
      this.tablist.setAttribute('hidden', '');

      this.panels.forEach((panel, index) => {
        const tab = this.tabs[index];
        if (!tab || !panel) return;

        const label = tab.textContent.trim();
        const contentId = `tabs-accordion-${++tabsAccordionCounter}`;

        const toggle = document.createElement('div');
        toggle.className = 'tabs__accordion-toggle';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'responsive-accordion__trigger';
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', contentId);

        const labelSpan = document.createElement('span');
        labelSpan.className = 'responsive-accordion__label';
        labelSpan.textContent = label;

        const icon = document.createElement('span');
        icon.className = 'responsive-accordion__icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = '<svg class="icon icon-chevron-down" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 7.5l5 5 5-5"/></svg>';

        button.append(labelSpan, icon);
        toggle.appendChild(button);

        const inner = document.createElement('div');
        inner.className = 'tabs__accordion-inner';
        [...panel.childNodes].forEach(child => inner.appendChild(child));
        panel.appendChild(inner);

        panel.removeAttribute('hidden');
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
        panel.removeAttribute('tabindex');
        panel.id = contentId;
        panel.classList.add('responsive-accordion__content');
        panel.setAttribute('data-accordion-content', '');

        if (index === 0) toggle.classList.add('tabs__accordion-toggle--first');

        button.addEventListener('click', this.handleAccordionTriggerClick);
        panel.insertAdjacentElement('beforebegin', toggle);

        this._accordionItems.push({ toggle, button, panel, inner, originalId: this.tabs[index].getAttribute('aria-controls') });
      });
    }

    teardownAccordion() {
      this._accordionItems.forEach(({ toggle, button, panel, inner, originalId }) => {
        button.removeEventListener('click', this.handleAccordionTriggerClick);
        toggle.remove();

        [...inner.childNodes].forEach(child => panel.insertBefore(child, inner));
        inner.remove();

        panel.id = originalId;
        panel.classList.remove('responsive-accordion__content');
        panel.removeAttribute('data-accordion-content');
        panel.removeAttribute('data-expanded');
      });
      this._accordionItems = [];

      this.tablist?.removeAttribute('hidden');

      this.panels.forEach(panel => {
        panel.setAttribute('role', 'tabpanel');
        const tab = this.tabs.find(t => t.getAttribute('aria-controls') === panel.id);
        if (tab) panel.setAttribute('aria-labelledby', tab.id);
        if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '0');
      });
    }

    handleAccordionTriggerClick(event) {
      const button = event.currentTarget;
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      const item = this._accordionItems.find(entry => entry.button === button);

      if (isExpanded) {
        button.setAttribute('aria-expanded', 'false');
        item?.panel.removeAttribute('data-expanded');
      } else {
        button.setAttribute('aria-expanded', 'true');
        item?.panel.setAttribute('data-expanded', 'true');
        if (item) {
          this._materializePanel(item.inner);
          this._fetchPanel(item.panel);
        }
      }
    }

    _materializePanel(container) {
      const template = container?.querySelector(':scope > template[data-tabs-panel-template]');
      if (!template) return;
      container.appendChild(template.content);
      template.remove();
    }

    async _fetchPanel(panel) {
      const src = panel?.dataset.panelSrc;
      const state = panel?.dataset.panelState;
      if (!src || state === 'loading' || state === 'ready') return;

      const target = panel.querySelector(':scope > .tabs__accordion-inner') ?? panel;
      const skeleton = document.createElement('div');
      skeleton.className = 'skeleton';
      skeleton.setAttribute('aria-hidden', 'true');
      skeleton.style.height = `${this.panels.find(p => !p.hidden)?.offsetHeight || 320}px`;

      panel.dataset.panelState = 'loading';
      panel.setAttribute('aria-busy', 'true');
      target.replaceChildren(skeleton);

      try {
        const response = await fetch(src);
        if (!response.ok) throw new Error(response.status);

        const doc = theme.utils.domParser.parseFromString(await response.text(), 'text/html');
        const content = doc.querySelector('[data-tabs-panel-content]');
        if (!content?.children.length) throw new Error('empty');

        target.replaceChildren(...content.childNodes);
        panel.dataset.panelState = 'ready';
        this.dispatchEvent(new CustomEvent('tabs:panel-loaded', {
          detail: { panel, panelId: panel.id },
          bubbles: true,
        }));
      } catch {
        const link = document.createElement('a');
        link.href = panel.dataset.panelErrorHref || '/';
        link.textContent = panel.dataset.panelErrorLabel || link.href;
        target.replaceChildren(link);
        panel.dataset.panelState = 'error';
      } finally {
        panel.removeAttribute('aria-busy');
      }
    }

    handleTabPrefetch(event) {
      const panelId = event.currentTarget.getAttribute('aria-controls');
      const panel = this.querySelector(`#${panelId}`);
      this._materializePanel(panel);
      this._fetchPanel(panel);
    }

    get activeTab() {
      return this.tabs.find(t => t.getAttribute('aria-selected') === 'true') ?? this.tabs[0];
    }

    activate(tab, moveFocus = true) {
      if (!tab) return;

      this._fetchPanel(this.querySelector(`#${tab.getAttribute('aria-controls')}`));

      this.tabs.forEach(t => {
        t.setAttribute('aria-selected', 'false');
        t.setAttribute('tabindex', '-1');
      });

      this.panels.forEach(p => p.setAttribute('hidden', ''));

      tab.setAttribute('aria-selected', 'true');
      tab.setAttribute('tabindex', '0');

      const panelId = tab.getAttribute('aria-controls');
      const panel = this.querySelector(`#${panelId}`);
      if (panel) {
        this._materializePanel(panel);
        panel.removeAttribute('hidden');
        if (moveFocus) panel.focus();
      }

      this.dispatchEvent(new CustomEvent('tabs:change', {
        detail: { tab, panelId },
        bubbles: true,
      }));
    }

    handleTabClick(event) {
      this.activate(event.currentTarget);
    }

    handleKeydown(event) {
      const index = this.tabs.indexOf(document.activeElement);
      if (index === -1) return;

      let next = null;

      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          next = this.tabs[(index + 1) % this.tabs.length];
          break;
        case 'ArrowLeft':
          event.preventDefault();
          next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
          break;
        case 'Home':
          event.preventDefault();
          next = this.tabs[0];
          break;
        case 'End':
          event.preventDefault();
          next = this.tabs[this.tabs.length - 1];
          break;
      }

      if (next) next.focus();
    }
  }

  customElements.define('theme-tabs', ThemeTabs);
}
